import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'rynora-theme';
const VALID: readonly ThemePreference[] = ['system', 'light', 'dark'] as const;

function readStored(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (VALID as readonly string[]).includes(raw)) {
      return raw as ThemePreference;
    }
  } catch {
    // localStorage may be unavailable (Safari private mode, sandboxed iframes).
  }
  return 'system';
}

function applyToDocument(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (pref === 'system') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', pref);
  }
}

/**
 * Returns the OS-level prefers-color-scheme. Falls back to 'light' when
 * `matchMedia` is missing (jsdom in tests, very old browsers, some
 * sandboxed iframes) so consumers never crash.
 */
function readSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Hook for reading and changing the active theme preference.
 *
 * Persists in localStorage. Setting 'system' removes the
 * `data-theme` attribute and lets the @media (prefers-color-scheme)
 * rules in `tokens.css` decide.
 */
export function useTheme(): {
  theme: ThemePreference;
  setTheme: (next: ThemePreference) => void;
  /** Resolved theme that's actually painted ('light' or 'dark'). */
  resolved: 'light' | 'dark';
} {
  const [theme, setThemeState] = useState<ThemePreference>(() => readStored());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => {
    const stored = readStored();
    if (stored === 'light') return 'light';
    if (stored === 'dark') return 'dark';
    return readSystemTheme();
  });

  // Apply on mount + whenever the preference changes.
  useEffect(() => {
    applyToDocument(theme);
    if (theme === 'light' || theme === 'dark') {
      setResolved(theme);
      return;
    }
    // matchMedia is missing in jsdom; resolve once and bail rather than
    // crash. Re-render only happens if the user picks light/dark explicitly.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setResolved(readSystemTheme());
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setResolved(mql.matches ? 'dark' : 'light');
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [theme]);

  // Sync across tabs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = readStored();
      setThemeState(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((next: ThemePreference) => {
    if (!(VALID as readonly string[]).includes(next)) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore — preference still applies for this session.
    }
    setThemeState(next);
  }, []);

  return { theme, setTheme, resolved };
}
